import type * as C from "@zooi/contracts";
import type { StatusKind } from "./ui/status-kinds.ts";

// The actions list and an action's progress (docs/design-system.md §0.4e,
// plan M7 step 7h), kept apart from the components so the rules are tested.

/** Lifecycle groups in order (architecture §5); the ones that stopped short start collapsed. */
export const ACTION_GROUPS = ["approved", "running", "yours", "delivered", "live", "due", "done", "stopped", "rejected", "failed", "expired"] as const;
export type ActionGroup = (typeof ACTION_GROUPS)[number];

const GROUP: Record<C.Action["status"], ActionGroup> = {
  queued: "approved", committed: "running", delivered: "delivered", live: "live", reading_due: "due", evaluated: "done",
  dismissed: "rejected", withdrawn: "rejected", failed: "failed", expired: "expired",
};
const GROUP_STATUS: Record<ActionGroup, StatusKind> = {
  approved: "approved", running: "running", delivered: "delivered", live: "observing", due: "due", done: "done",
  yours: "yours", stopped: "stopped", rejected: "rejected", failed: "failed", expired: "expired",
};
const STOPPED: readonly ActionGroup[] = ["rejected", "failed", "expired"];

export type ExecutionAction = Pick<C.Action, "status"> & Partial<Pick<C.Action, "executor" | "execution">>;

/** Authorization is not execution. Only a live run can be in progress. */
export function executionStatus(action: ExecutionAction): "approved" | "running" | "yours" | "stopped" | "failed" | "expired" | "delivered" | null {
  if (action.status !== "committed") return null;
  const work = action.execution;
  if (!work) return action.executor === "agent" ? "approved" : null;
  switch (work.status) {
    case "approved": return work.claimable ? "approved" : "stopped";
    case "running": return !work.run ? "approved" : work.run.status === "working" ? "running" : ["input_required", "auth_required"].includes(work.run.status) ? "yours" : "stopped";
    case "needs_input": return "yours";
    case "reconciling": return "stopped";
    case "failed": return "failed";
    case "expired": return "expired";
    case "cancelled": return "stopped";
    case "delivered": case "verifying": case "verified": return "delivered";
    default: return "approved";
  }
}

export const groupOfAction = (action: ExecutionAction): ActionGroup => executionStatus(action) ?? GROUP[action.status];
export const groupStatus = (group: ActionGroup): StatusKind => GROUP_STATUS[group];
export const collapsedByDefault = (group: ActionGroup) => STOPPED.includes(group);

/** The groups the person folded or unfolded on this device; anything else is invalid and ignored. */
export function readCollapsed(raw: string | null): Partial<Record<ActionGroup, boolean>> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter((entry): entry is [ActionGroup, boolean] =>
      (ACTION_GROUPS as readonly string[]).includes(entry[0]) && typeof entry[1] === "boolean"));
  } catch {
    return {};
  }
}
export const isCollapsed = (state: Partial<Record<ActionGroup, boolean>>, group: ActionGroup) => state[group] ?? collapsedByDefault(group);

export interface ActionFilter { campaign: string | null; channel: string | null; executor: C.ActionExecutor | null }

/** The actions a filter keeps; a campaign filter keeps only actions of that campaign. */
export function filterActions<T extends Pick<C.Action, "id" | "channel" | "executor">>(actions: readonly T[], filter: ActionFilter, campaigns: ReadonlyMap<string, { id: string }>): T[] {
  return actions.filter((action) =>
    (!filter.campaign || campaigns.get(action.id)?.id === filter.campaign)
    && (!filter.channel || action.channel === filter.channel)
    && (!filter.executor || action.executor === filter.executor));
}

/** Non-empty groups in lifecycle order, newest first within a group. */
export function groupActions<T extends ExecutionAction & Pick<C.Action, "created_at">>(actions: readonly T[]): { group: ActionGroup; actions: T[] }[] {
  return ACTION_GROUPS
    .map((group) => ({ group, actions: actions.filter((action) => groupOfAction(action) === group).sort((a, b) => b.created_at.localeCompare(a.created_at)) }))
    .filter((entry) => entry.actions.length > 0);
}

export interface Stage { key: "delivered" | "live" | "observed" | "reading"; status: "complete" | "current" | "upcoming"; at: string | null }

/**
 * The four layers (product vision §8.1): delivered, live, observed, read. A
 * layer not reached yet is upcoming, never a failure; a closed action shows
 * where it stopped and nothing as current.
 */
export function actionStages(action: ExecutionAction & Pick<C.Action, "delivered_at" | "activated_at" | "matures_at" | "reading">, now: number): Stage[] {
  const matured = action.status === "reading_due" || action.status === "evaluated" || (action.matures_at !== null && Date.parse(action.matures_at) <= now);
  const done = [
    { key: "delivered" as const, complete: action.delivered_at !== null || action.activated_at !== null, at: action.delivered_at },
    { key: "live" as const, complete: action.activated_at !== null, at: action.activated_at },
    { key: "observed" as const, complete: action.activated_at !== null && matured, at: matured ? action.matures_at : null },
    { key: "reading" as const, complete: action.status === "evaluated", at: action.reading?.confirmed_at ?? null },
  ];
  const closed = STOPPED.includes(groupOfAction(action)) || groupOfAction(action) === "stopped";
  const firstOpen = done.findIndex((stage) => !stage.complete);
  return done.map((stage, index) => ({
    key: stage.key,
    at: stage.complete ? stage.at : null,
    status: stage.complete ? "complete" : !closed && index === firstOpen ? "current" : "upcoming",
  }));
}

/** Delivery unlocks the compact history; operational acceptance has no observation step. */
export function actionProgress(action: Parameters<typeof actionStages>[0] & Pick<C.ActionDetail, "measurement_plan">, now: number): { completed: Stage[]; next: Stage | null } {
  const layers = actionStages(action, now);
  const visible = layers.filter((stage) => action.measurement_plan.mode !== "operational" || stage.key !== "observed");
  const completed = visible.filter((stage) => stage.status === "complete");
  const next = completed.length > 0 && layers.some((stage) => stage.status === "current")
    ? visible.find((stage) => stage.status !== "complete") ?? null : null;
  return { completed, next };
}

/** Which campaign each action belongs to, from the campaigns' own lists. */
export function campaignOfAction(campaigns: readonly Pick<C.Campaign, "id" | "title" | "action_ids">[]): Map<string, { id: string; title: string }> {
  const out = new Map<string, { id: string; title: string }>();
  for (const campaign of campaigns) for (const id of campaign.action_ids) out.set(id, { id: campaign.id, title: campaign.title });
  return out;
}

/** The plan action an approval turned into this action, and the server's authorship check for it. */
export function planActionOf(plan: Pick<C.AgentPlan, "applied" | "body" | "checked"> | undefined, actionId: string) {
  const ref = plan?.applied ? Object.entries(plan.applied.actions).find((entry) => entry[1] === actionId)?.[0] : undefined;
  const action = ref ? plan?.body.actions.find((candidate) => candidate.ref === ref) : undefined;
  return plan && action ? { action, check: plan.checked.authorship.find((entry) => entry.ref === action.ref) ?? null } : null;
}

/** The next time that matters on a row: when a live action's window matures, or when an open one expires. */
export function nextTime(action: Pick<C.Action, "status" | "matures_at" | "expires_at">): { kind: "matures" | "expires"; at: string } | null {
  if (action.status === "live" && action.matures_at) return { kind: "matures", at: action.matures_at };
  if ((action.status === "queued" || action.status === "committed") && action.expires_at) return { kind: "expires", at: action.expires_at };
  return null;
}
